import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface ManagedProfileData {
  isManagedProfile: boolean;
  relationshipType: string | null;
  ownerId: string | null;
}

export const useManagedProfile = (targetUserId?: string) => {
  const { user } = useAuth();

  const { data: managedProfileData, isLoading } = useQuery({
    queryKey: ['managed-profile-check', user?.id, targetUserId],
    queryFn: async (): Promise<ManagedProfileData> => {
      if (!user?.id || !targetUserId) {
        return { isManagedProfile: false, relationshipType: null, ownerId: null };
      }

      // Check if the current user owns the target profile
      const { data, error } = await supabase
        .from('profile_ownerships')
        .select('relationship_type, owner_id')
        .eq('owner_id', user.id)
        .eq('owned_profile_id', targetUserId)
        .maybeSingle();

      if (error) {
        console.error('Managed profile check error:', error);
        return { isManagedProfile: false, relationshipType: null, ownerId: null };
      }

      return {
        isManagedProfile: !!data,
        relationshipType: data?.relationship_type || null,
        ownerId: data?.owner_id || null
      };
    },
    enabled: !!user?.id && !!targetUserId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    isManagedProfile: managedProfileData?.isManagedProfile || false,
    relationshipType: managedProfileData?.relationshipType || null,
    ownerId: managedProfileData?.ownerId || null,
    isLoading
  };
};